import { Package, CheckCircle, Search, MapPin, ArrowRight, Wrench } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { SEOHead } from "../SEOHead";
import { Header } from "../Header";
import { Footer } from "../Footer";
import { FloatingContactButtons } from "../FloatingContactButtons";
import { companyInfo } from "../../data/mock";

export const PartsSalesPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead 
        title="Used Car Parts Hackney | Salvaged Auto Parts East London | Quality Vehicle Parts"
        description="Quality used car parts & salvaged auto parts in Hackney & East London. Engines, gearboxes, body panels, lights, doors. Professional dismantling. Call 07492 639223 for parts."
        keywords="used car parts hackney, salvaged auto parts, second hand car parts, vehicle parts london, car parts east london, engine parts, gearbox parts, body panels"
      />
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-900 via-amber-800 to-yellow-800 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-amber-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              🔧 Quality Used Parts
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Used Car Parts & Salvaged Auto Parts in Hackney
            </h1>
            <p className="text-xl text-amber-100 mb-8">
              Quality used car parts from professionally dismantled vehicles in Hackney & East London. Engines, gearboxes, body panels, lights, doors, and more. Save money without compromising quality.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${companyInfo.phone}`}>
                <Button size="lg" className="bg-white text-amber-900 hover:bg-slate-100 text-lg px-8 py-6">
                  <Package className="w-5 h-5 mr-2" />
                  Call: {companyInfo.phone}
                </Button>
              </a>
              <Link to="/#quote">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-amber-900 text-lg px-8 py-6">
                  Request Part Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            <div className="text-center">
              <p className="text-3xl font-bold text-amber-600">Quality</p>
              <p className="text-sm text-slate-600">Tested Parts</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-amber-600">Warranty</p>
              <p className="text-sm text-slate-600">On All Parts</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-amber-600">Fast</p>
              <p className="text-sm text-slate-600">Same Day Collection</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-amber-600">Local</p>
              <p className="text-sm text-slate-600">Hackney Based</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Quality Used Car Parts from Professional Dismantling
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Ovawell Autos supplies quality used car parts and salvaged auto parts across Hackney and East London. All parts come from professionally dismantled vehicles, are tested where applicable, and come with warranty. Perfect for repair businesses, mechanics, and vehicle owners looking to save money on genuine quality parts.
            </p>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Parts We Supply
            </h3>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "Complete engines (petrol & diesel)",
                "Gearboxes and transmissions",
                "Doors (front, rear, boot)",
                "Bonnets and tailgates",
                "Front & rear bumpers",
                "Headlights and tail lights",
                "Wing mirrors and indicators",
                "Alloy wheels and tyres",
                "Interior parts (seats, dashboards)",
                "Suspension components",
                "Brake components",
                "Exhaust systems",
                "Radiators and cooling parts",
                "ECU and electrical components",
                "Steering components",
                "Body panels and trim"
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <Wrench className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Why Choose Our Used Parts?
            </h3>
            <div className="space-y-4 mb-8">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-8 h-8 text-amber-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">Professional Dismantling</h4>
                      <p className="text-slate-600">
                        All parts are carefully removed by experienced technicians using proper tools. We don't damage parts during removal - each component is treated with care.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Package className="w-8 h-8 text-amber-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">Quality Testing</h4>
                      <p className="text-slate-600">
                        Where applicable, parts are tested before sale. Electrical components are bench-tested, mechanical parts are inspected for wear. We only sell parts we'd use ourselves.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-8 h-8 text-amber-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">Warranty Included</h4>
                      <p className="text-slate-600">
                        All parts come with warranty for peace of mind. If a part is faulty, we'll replace it or refund you - no questions asked. Our reputation depends on quality.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Search className="w-8 h-8 text-amber-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">Part Sourcing Service</h4>
                      <p className="text-slate-600">
                        Can't find the part you need? We source hard-to-find parts from our network of dismantlers and suppliers. Just tell us what you need - we'll find it.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              How to Order Parts
            </h3>
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-amber-600">1</span>
                  </div>
                  <h4 className="font-bold mb-2">Contact Us</h4>
                  <p className="text-sm text-slate-600">Call or message with your part requirements</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-amber-600">2</span>
                  </div>
                  <h4 className="font-bold mb-2">Get Quote</h4>
                  <p className="text-sm text-slate-600">Receive competitive price and availability</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-amber-600">3</span>
                  </div>
                  <h4 className="font-bold mb-2">Collect or Deliver</h4>
                  <p className="text-sm text-slate-600">Pickup from Hackney or we deliver</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-amber-600">4</span>
                  </div>
                  <h4 className="font-bold mb-2">Warranty Covered</h4>
                  <p className="text-sm text-slate-600">All parts come with warranty protection</p>
                </CardContent>
              </Card>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Who We Supply Parts To
            </h3>
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold text-lg text-slate-900 mb-2">Repair Garages</h4>
                  <p className="text-sm text-slate-600">Trade accounts available. Reliable parts supply for your customer repairs at competitive prices.</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold text-lg text-slate-900 mb-2">Mobile Mechanics</h4>
                  <p className="text-sm text-slate-600">Quick collection or delivery. Stock up on common parts or order specific components as needed.</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold text-lg text-slate-900 mb-2">Vehicle Owners</h4>
                  <p className="text-sm text-slate-600">Save money on repairs. Quality parts at fraction of new prices. Perfect for older vehicles.</p>
                </CardContent>
              </Card>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Popular Parts We Stock
            </h3>
            <Card className="mb-8 bg-amber-50 border-amber-200">
              <CardContent className="p-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-bold text-slate-900 mb-2">Mechanical Parts:</h4>
                    <ul className="text-sm text-slate-700 space-y-1">
                      <li>• Engines (most makes/models)</li>
                      <li>• Gearboxes (manual & automatic)</li>
                      <li>• Turbochargers</li>
                      <li>• Alternators & starters</li>
                      <li>• Fuel pumps & injectors</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-2">Body & Trim:</h4>
                    <ul className="text-sm text-slate-700 space-y-1">
                      <li>• Front & rear bumpers</li>
                      <li>• Doors (all positions)</li>
                      <li>• Bonnets & tailgates</li>
                      <li>• Lights (headlights, tail lights)</li>
                      <li>• Wing mirrors (electric & manual)</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Frequently Asked Questions
            </h3>
            <div className="space-y-4 mb-8">
              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold text-lg mb-2">Do used parts come with warranty?</h4>
                  <p className="text-slate-600">
                    Yes, all parts come with warranty. The duration varies by part type, but we stand behind everything we sell. Faulty parts are replaced or refunded.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold text-lg mb-2">Are parts tested before sale?</h4>
                  <p className="text-slate-600">
                    Where applicable, yes. Electrical components are bench-tested, mechanical parts are inspected thoroughly. We only sell quality parts in good working condition.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold text-lg mb-2">Can you source specific parts?</h4>
                  <p className="text-slate-600">
                    Absolutely. If we don't have a part in stock, we can source it from our network of suppliers. Just provide vehicle details and part requirements.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold text-lg mb-2">Do you deliver parts?</h4>
                  <p className="text-slate-600">
                    Yes. Free collection from our Hackney location, or we can deliver across East London for a small fee. Nationwide delivery also available for larger orders.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-amber-900 to-orange-900 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Need Quality Used Car Parts?
          </h2>
          <p className="text-xl text-amber-100 mb-8 max-w-2xl mx-auto">
            Save money without compromising quality. Call now for parts availability and pricing.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${companyInfo.phone}`}>
              <Button size="lg" className="bg-white text-amber-900 hover:bg-slate-100 text-lg px-8 py-6">
                <Package className="w-5 h-5 mr-2" />
                Call {companyInfo.phone}
              </Button>
            </a>
            <Link to="/#quote">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-amber-900 text-lg px-8 py-6">
                Request Part Quote
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingContactButtons />
    </div>
  );
};
